/*
 * Figma `122:213` — TODAY-03 · Staff & Roles.
 *
 * `ManagementTemplate`: the access list, the role panel beside it, and the check-in record under
 * both.
 *
 * The frame draws a membership system — named members with an Admin tier, an access-review cycle
 * that comes due monthly, and an "Export audit" button. This product has none of those. What it has
 * is **tokens**: a string you hand to someone, with a label, a role, an expiry and a last-used
 * stamp. There is no member record to review and no account to promote, so the tiles count the
 * things that are real and that someone would actually act on — what is still good, what lapses
 * this week, and what was issued and has never been used, which is either a token that never
 * arrived or one that is loose.
 *
 * The frame's right-hand panel asks the right question — "Full access ทำอะไรได้บ้าง" — and
 * `roleAccess` answers it off `destinations.ts` rather than from a list typed out beside it. A
 * hand-written matrix is a second source that drifts; derived, it cannot claim a role opens a
 * screen the console would refuse it.
 *
 * One piece of copy is corrected rather than carried over. The classic panel says of a new token
 * "คัดลอกตอนนี้ จะไม่แสดงให้ดูอีก". That is not true — the DTO carries the decrypted value for an
 * owner, and this screen reveals it again on demand. A warning that the system does not enforce
 * teaches people to ignore the ones it does.
 */
import { useState } from 'react';
import {
  Alert, AuditTimeline, Button, Card, ConfirmDialog, CredentialReveal, DataTable, Drawer,
  FilterChips, FormDialog, KpiCard, KpiGrid, ManagementTemplate, ResultSummary, SearchField,
  Select, StatusBadge, TextField,
} from '../..';
import type { AuditEvent, BadgeTone, Column, FilterChip } from '../..';
import type { Destination } from '../../destinations';
import type { CheckinTokenDto } from '../../../api';
import type {
  ExpiryChoice, NewTokenDraft, StaffTokensState, TokenRole, TokenState,
} from './useStaffTokens';
import {
  checkinTokenState, EXPIRING_SOON_DAYS, expiresWithin, roleAccess, TOKEN_ROLES, useStaffTokens,
} from './useStaffTokens';

const STATE_LABEL: Record<TokenState, string> = {
  active: 'ใช้งานได้', revoked: 'เพิกถอนแล้ว', expired: 'หมดอายุแล้ว', used_up: 'ใช้ครบแล้ว',
};
const STATE_TONE: Record<TokenState, BadgeTone> = {
  active: 'success', revoked: 'danger', expired: 'warning', used_up: 'neutral',
};

const ROLE_LABEL: Record<TokenRole, string> = {
  checkin: 'เช็กอินหน้างาน', pos: 'จุดจำหน่าย', sales: 'ฝ่ายขาย',
};

const EXPIRY_LABEL: Record<ExpiryChoice, string> = {
  '24h': '24 ชั่วโมง', '7d': '7 วัน', '30d': '30 วัน', never: 'ไม่หมดอายุ',
};

const dt = (iso: string) => new Date(iso).toLocaleString('th-TH');
const day = (iso: string) => new Date(iso).toLocaleDateString('th-TH');

type Filter = 'all' | 'active' | 'expiring' | 'inactive';

export function StaffRolesScreen({ destination, state, permitted, role, eventSlug }: {
  destination: Destination;
  state: StaffTokensState;
  permitted: boolean;
  role?: string | null;
  eventSlug?: string;
}) {
  const {
    tokens, log, summary, loading, loadError, logError, createError, busy, justCreated,
    dismissCreated, create, revoke, reload,
  } = state;

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<CheckinTokenDto | null>(null);
  const [draft, setDraft] = useState<NewTokenDraft>({ holderLabel: '', role: 'checkin', expiry: 'never' });

  const open = openId ? tokens.find((t) => t.id === openId) ?? null : null;
  /*
   * TICK-177 — the full value exists in the browser only between a create and its dismissal, and
   * only for that one token. List rows never carry it, so every other row's drawer has nothing to
   * reveal and says why.
   */
  const oneTimeFor = (t: CheckinTokenDto | null) =>
    t && justCreated && justCreated.id === t.id && checkinTokenState(t) === 'active' ? justCreated.token : null;
  const openOneTime = oneTimeFor(open);
  const closeDrawer = () => {
    // Closing the drawer of the just-issued token ends its one-time reveal as well.
    if (openOneTime) dismissCreated();
    setOpenId(null);
  };

  const needle = query.trim().toLowerCase();
  const rows = tokens.filter((t) => {
    const s = checkinTokenState(t);
    if (filter === 'active' && s !== 'active') return false;
    if (filter === 'expiring' && !expiresWithin(t)) return false;
    if (filter === 'inactive' && s === 'active') return false;
    if (!needle) return true;
    return t.holderLabel.toLowerCase().includes(needle) || t.tokenPreview.toLowerCase().includes(needle);
  });

  const chips: readonly FilterChip<Filter>[] = [
    { key: 'all', label: 'ทั้งหมด', count: tokens.length },
    { key: 'active', label: 'ใช้งานได้', count: summary.active },
    { key: 'expiring', label: `ใกล้หมดอายุ`, count: summary.expiringSoon },
    { key: 'inactive', label: 'ใช้ไม่ได้แล้ว', count: tokens.length - summary.active },
  ];

  const columns: Column<CheckinTokenDto>[] = [
    {
      key: 'holder',
      header: 'ผู้ถือ',
      cell: (t) => (
        <span className="jt-cell-stack">
          <span>{t.holderLabel || '(ไม่มีชื่อ)'}</span>
          <span className="jt-cell-sub">ออกเมื่อ {day(t.createdAt)}</span>
        </span>
      ),
    },
    { key: 'role', header: 'สิทธิ์', width: '150px', cell: (t) => ROLE_LABEL[t.role] },
    {
      key: 'token',
      header: 'Token',
      width: '150px',
      /* The preview only. The value itself is a deliberate act, in the drawer. */
      cell: (t) => <span className="jt-mono">{t.tokenPreview}</span>,
    },
    {
      key: 'used',
      header: 'ใช้ล่าสุด',
      width: '190px',
      cell: (t) => (t.lastUsedAt
        ? dt(t.lastUsedAt)
        : <span className="jt-cell-sub">ยังไม่เคยใช้</span>),
    },
    {
      key: 'state',
      header: 'สถานะ',
      width: '150px',
      cell: (t) => {
        const s = checkinTokenState(t);
        return (
          <span className="jt-cell-stack">
            <StatusBadge tone={STATE_TONE[s]}>{STATE_LABEL[s]}</StatusBadge>
            {expiresWithin(t) && t.expiresAt && (
              <span className="jt-cell-sub">หมดอายุ {day(t.expiresAt)}</span>
            )}
          </span>
        );
      },
    },
  ];

  const logEvents: AuditEvent[] = (log?.entries ?? []).map((e) => ({
    id: e.id,
    actor: e.actor,
    action: 'เช็กอินบัตร',
    target: e.detail,
    at: dt(e.createdAt),
  }));

  return (
    <ManagementTemplate
      gate={{ destination, permitted, role, loading, error: loadError || null, onRetry: reload }}
      summary={
        <KpiGrid>
          <KpiCard
            label="Token ที่ใช้งานได้"
            value={summary.active.toLocaleString('th-TH')}
            meta={`จากทั้งหมด ${summary.total.toLocaleString('th-TH')} · เพิกถอนแล้ว ${summary.revoked.toLocaleString('th-TH')}`}
          />
          <KpiCard
            label="แยกตามสิทธิ์"
            value={`${summary.byRole.checkin} · ${summary.byRole.pos} · ${summary.byRole.sales}`}
            meta="เช็กอิน · จุดจำหน่าย · ฝ่ายขาย (เฉพาะที่ใช้งานได้)"
          />
          <KpiCard
            label={`ใกล้หมดอายุใน ${EXPIRING_SOON_DAYS} วัน`}
            value={summary.expiringSoon.toLocaleString('th-TH')}
            tone={summary.expiringSoon > 0 ? 'warning' : 'neutral'}
            meta={summary.nextExpiry
              ? `อันแรกหมดอายุ ${day(summary.nextExpiry)}`
              : 'ไม่มี Token ที่ตั้งวันหมดอายุไว้'}
          />
          <KpiCard
            label="ออกแล้วแต่ยังไม่เคยใช้"
            value={summary.neverUsed.toLocaleString('th-TH')}
            tone={summary.neverUsed > 0 ? 'warning' : 'neutral'}
            meta="ถ้าไม่ได้เพิ่งออกให้ แปลว่าอาจไปไม่ถึงคนที่ควรได้ หรือหลุดอยู่ที่ไหนสักแห่ง"
          />
        </KpiGrid>
      }
      toolbar={
        <>
          <SearchField value={query} onChange={setQuery} label="ค้นหาผู้ถือ Token" />
          <FilterChips chips={chips} active={filter} onSelect={setFilter} label="กรองตามสถานะ" />
          <Button variant="primary" onClick={() => setCreating(true)}>ออก Token ใหม่</Button>
        </>
      }
      items={
        <>
          {justCreated && (
            <div data-testid="new-token-handoff">
              <Alert tone="success" title={`ออก Token ให้ "${justCreated.holderLabel}" แล้ว`}>
                <p>
                  กด “แสดงค่าเต็ม” เพื่อดูและคัดลอกค่า Token ส่งให้ผู้ถือ — ค่าเต็มดูได้ครั้งเดียว ตอนนี้เท่านั้น
                  เมื่อปิดข้อความนี้ ระบบจะไม่แสดงค่าเต็มอีก ถ้าหายต้องเพิกถอนแล้วออก Token ใหม่
                </p>
                <CredentialReveal
                  label={`Token · ${ROLE_LABEL[justCreated.role]}`}
                  masked={tokens.find((t) => t.id === justCreated.id)?.tokenPreview ?? '••••••••'}
                  reveal={() => justCreated.token}
                />
                <div className="jt-drawer-guard">
                  <Button size="sm" variant="ghost" onClick={dismissCreated}>ปิดและล้างค่า</Button>
                </div>
              </Alert>
            </div>
          )}

          <ResultSummary shown={rows.length} total={tokens.length} label="Token" />

          <div className="jt-with-rail">
            <div className="jt-section">
              <DataTable
                label="Token ของทีมงาน"
                columns={columns}
                rows={rows}
                rowKey={(t) => t.id}
                filtered={filter !== 'all' || needle !== ''}
                onClearFilters={() => { setFilter('all'); setQuery(''); }}
                emptyTitle="ยังไม่มี Token"
                emptyText="ออก Token แรกให้ทีมงานด้วยปุ่มด้านบน"
                onRowClick={(t) => setOpenId(t.id)}
                activeRowKey={openId}
                card={(t) => (
                  <span className="jt-cell-stack">
                    <span>{t.holderLabel || '(ไม่มีชื่อ)'}</span>
                    <span className="jt-cell-sub">{ROLE_LABEL[t.role]} · {STATE_LABEL[checkinTokenState(t)]}</span>
                  </span>
                )}
              />
            </div>

            <div className="jt-section">
              <Card title="Token แต่ละแบบเปิดอะไรได้" sub="อ่านจากทะเบียนหน้าจอที่คอนโซลใช้จริง ไม่ใช่รายการที่พิมพ์ไว้ข้าง ๆ">
                {TOKEN_ROLES.map((r) => {
                  const access = roleAccess(r);
                  return (
                    <div key={r} className="jt-role-block">
                      <div className="jt-role-head">
                        <span className="jt-role-name">{access.label}</span>
                        <StatusBadge tone={access.consoleRole === null ? 'neutral' : 'accent'}>
                          {access.consoleRole === null ? 'ไม่เข้าคอนโซล' : `${access.destinations.length} หน้า`}
                        </StatusBadge>
                      </div>
                      <p className="jt-role-surface">{access.surface}</p>
                      <p className="jt-mono jt-role-path">{access.path(eventSlug)}</p>
                      {access.consoleRole === null ? (
                        <p className="jt-role-note">
                          การขายบัตรหน้างานไม่ใช่การรับคนเข้างาน และไม่ใช่การอ่านหลังบ้าน — Token นี้จึงเปิดหน้าคอนโซลไม่ได้เลยสักหน้า
                        </p>
                      ) : (
                        <p className="jt-role-note">
                          {access.destinations.map((d) => d.navLabel).join(' · ')}
                        </p>
                      )}
                    </div>
                  );
                })}
              </Card>
            </div>
          </div>

          <Card title="สรุปการเช็กอินรายคน" sub="จำนวน Wristband (คน) ที่สตาฟแต่ละคนแจกไปแล้ว">
            {logError ? (
              <Alert tone="danger" title="โหลดบันทึกการเช็กอินไม่สำเร็จ">{logError}</Alert>
            ) : !log || log.byStaff.length === 0 ? (
              <p className="jt-field-hint">ยังไม่มีการเช็กอิน</p>
            ) : (
              <ul className="jt-checks">
                {log.byStaff.map((s) => (
                  <li key={s.actor} className="jt-check">
                    <div className="jt-check-copy">
                      <p className="jt-check-label">{s.actor}</p>
                      <p className="jt-check-detail">ล่าสุด {dt(s.lastCheckedInAt)} · {s.count.toLocaleString('th-TH')} ออเดอร์</p>
                    </div>
                    <span className="jt-check-trail jt-mono">{(s.people ?? s.count).toLocaleString('th-TH')} คน</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="ประวัติการเช็กอินล่าสุด" sub="เรียงจากล่าสุดไปเก่าสุด">
            <AuditTimeline
              events={logEvents}
              label="ประวัติการเช็กอิน"
              empty="ยังไม่มีรายการเช็กอิน"
            />
          </Card>
        </>
      }
      detail={
        <>
          <Drawer
            open={open !== null}
            onClose={closeDrawer}
            title={open?.holderLabel || 'Token'}
          >
            {open && (
              <>
                <CredentialReveal
                  label={`Token · ${ROLE_LABEL[open.role]}`}
                  masked={open.tokenPreview}
                  status={checkinTokenState(open) === 'active' ? 'active'
                    : checkinTokenState(open) === 'revoked' ? 'revoked' : 'expired'}
                  /* Only the token issued in this visit, from the create response held in memory. */
                  reveal={openOneTime ? () => openOneTime : undefined}
                  expiryNote={open.expiresAt ? `หมดอายุ ${dt(open.expiresAt)}` : 'ไม่หมดอายุ'}
                  lastUsedNote={open.lastUsedAt ? `ใช้ล่าสุด ${dt(open.lastUsedAt)}` : 'ยังไม่เคยใช้'}
                  onRevoke={checkinTokenState(open) === 'active' ? () => setConfirmRevoke(open) : undefined}
                />
                {openOneTime ? (
                  <p className="jt-field-hint">
                    เพิ่งออก Token นี้ — ดูและคัดลอกค่าเต็มได้จนกว่าจะปิดหน้าต่างนี้หรือข้อความ “ออก Token แล้ว”
                  </p>
                ) : checkinTokenState(open) === 'active' && (
                  <Alert tone="info" title="ค่าเต็มแสดงเฉพาะตอนออก Token">
                    ระบบไม่เก็บค่าเต็มไว้ให้ดูซ้ำ — ถ้าผู้ถือยังไม่ได้รับหรือทำหาย ให้เพิกถอนแล้วออก Token ใหม่
                  </Alert>
                )}
                <Card title="Token นี้เปิดอะไรได้">
                  <p className="jt-field-hint">{roleAccess(open.role).surface}</p>
                  <p className="jt-mono jt-role-path">{roleAccess(open.role).path(eventSlug)}</p>
                </Card>
              </>
            )}
          </Drawer>

          <FormDialog
            open={creating}
            onClose={() => setCreating(false)}
            onSubmit={() => {
              void create(draft).then((ok) => {
                if (!ok) return;
                setCreating(false);
                setDraft({ holderLabel: '', role: 'checkin', expiry: 'never' });
              });
            }}
            title="ออก Token ใหม่"
            description="Token หนึ่งอันต่อหนึ่งคน — ถ้าใช้ร่วมกัน จะบอกไม่ได้ว่าใครเป็นคนเช็กอิน"
            submitLabel="ออก Token"
            busy={busy}
            error={createError || null}
          >
            <TextField
              label="ชื่อผู้ถือ"
              value={draft.holderLabel}
              onChange={(e) => setDraft({ ...draft, holderLabel: e.target.value })}
              placeholder="เช่น เจ้าหน้าที่ประตู 1 — คุณเอ"
              requiredMark
              hint="ชื่อนี้จะขึ้นในประวัติการเช็กอิน ตั้งให้รู้ว่าเป็นใคร"
            />
            <Select
              label="สิทธิ์"
              value={draft.role}
              onChange={(e) => setDraft({ ...draft, role: e.target.value as TokenRole })}
              options={TOKEN_ROLES.map((r) => ({ value: r, label: roleAccess(r).label }))}
              hint={roleAccess(draft.role).consoleRole === null
                ? 'เปิดได้เฉพาะหน้าจุดจำหน่าย — ไม่เห็นหลังบ้านเลย'
                : `เปิดคอนโซลได้ ${roleAccess(draft.role).destinations.length} หน้า`}
            />
            <Select
              label="อายุการใช้งาน"
              value={draft.expiry}
              onChange={(e) => setDraft({ ...draft, expiry: e.target.value as ExpiryChoice })}
              options={(Object.keys(EXPIRY_LABEL) as ExpiryChoice[]).map((k) => ({ value: k, label: EXPIRY_LABEL[k] }))}
              hint="Token ที่ไม่หมดอายุจะใช้ได้จนกว่าจะถูกเพิกถอน"
            />
          </FormDialog>

          <ConfirmDialog
            open={confirmRevoke !== null}
            onClose={() => setConfirmRevoke(null)}
            onConfirm={() => {
              const target = confirmRevoke;
              setConfirmRevoke(null);
              closeDrawer();
              if (target) void revoke(target);
            }}
            title={`เพิกถอน Token ของ ${confirmRevoke?.holderLabel || '(ไม่มีชื่อ)'}`}
            confirmLabel="เพิกถอน"
            busy={busy}
            consequences={[
              'คนที่ถือ Token นี้จะเข้าใช้งานไม่ได้ทันที ถ้ากำลังยืนอยู่หน้าประตูจะเช็กอินต่อไม่ได้',
              'ประวัติการเช็กอินที่ทำไปแล้วยังอยู่ครบ ไม่ถูกลบไปด้วย',
              'เพิกถอนแล้วเปิดกลับไม่ได้ — ถ้ายังต้องใช้ ต้องออก Token ใหม่ให้',
            ]}
          />
        </>
      }
    />
  );
}

export function StaffRolesDestination({ token, eventSlug, destination, permitted, role }: {
  token: string;
  eventSlug?: string;
  destination: Destination;
  permitted: boolean;
  role?: string | null;
}) {
  const state = useStaffTokens(token, eventSlug);
  return (
    <StaffRolesScreen
      destination={destination}
      state={state}
      permitted={permitted}
      role={role}
      eventSlug={eventSlug}
    />
  );
}
